import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { Package, Truck, CheckCircle, Clock, MapPin } from "lucide-react";

const ORDER_STEPS = [
  { key: "paid", label: "Order Confirmed", icon: CheckCircle },
  { key: "processing", label: "Processing", icon: Package },
  { key: "shipped", label: "Dispatched", icon: Truck },
  { key: "out_for_delivery", label: "Out for Delivery", icon: MapPin },
  { key: "delivered", label: "Delivered", icon: CheckCircle }
];

const OrderTracking = ({ orderId, currentStatus }) => {
  const { token } = useAuth();
  const [tracking, setTracking] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTracking();
  }, [orderId]);

  const fetchTracking = async () => {
    try {
      const response = await axios.get(`${API}/orders/${orderId}/tracking`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTracking(response.data);
    } catch (error) {
      console.error("Failed to fetch tracking");
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="text-sm text-[#5C5D5C]">Loading tracking...</div>;

  const statusIndex = ORDER_STEPS.findIndex(s => s.key === (tracking?.status || currentStatus));

  return (
    <div data-testid="order-tracking">
      {/* Progress Steps */}
      <div className="flex items-center justify-between mb-6">
        {ORDER_STEPS.map((step, index) => {
          const Icon = step.icon;
          const isComplete = index <= statusIndex;
          const isCurrent = index === statusIndex;

          return (
            <div key={step.key} className="flex flex-col items-center flex-1 relative">
              {index > 0 && (
                <div className={`absolute top-5 -left-1/2 w-full h-0.5 ${
                  index <= statusIndex ? "bg-[#3E6656]" : "bg-[#EAE6DF]"
                }`} />
              )}
              <div className={`w-10 h-10 rounded-full flex items-center justify-center z-10 transition-all ${
                isComplete ? "bg-[#3E6656] text-white" : "bg-[#EAE6DF] text-[#8E8F8E]"
              } ${isCurrent ? "ring-2 ring-[#3E6656]/30 ring-offset-2" : ""}`}>
                <Icon size={18} />
              </div>
              <span className={`text-xs mt-2 text-center ${isComplete ? "text-[#1A1B1A] font-medium" : "text-[#8E8F8E]"}`}>
                {step.label}
              </span>
            </div>
          );
        })}
      </div>

      {/* Tracking History */}
      {tracking?.tracking_history?.length > 0 && (
        <div className="border-t border-[#EAE6DF] pt-4 mt-4">
          <p className="text-sm font-medium text-[#1A1B1A] mb-3">Tracking History</p>
          <div className="space-y-2">
            {tracking.tracking_history.slice().reverse().map((entry, index) => (
              <div key={index} className="flex gap-3 text-sm" data-testid={`tracking-entry-${index}`}>
                <div className="w-2 h-2 rounded-full bg-[#20C997] mt-1.5 flex-shrink-0"></div>
                <div>
                  <p className="text-[#1A1B1A] capitalize">{entry.status.replace(/_/g, " ")}</p>
                  {entry.note && <p className="text-[#5C5D5C] text-xs">{entry.note}</p>}
                  <p className="text-[#8E8F8E] text-xs">
                    {new Date(entry.timestamp).toLocaleString("en-GB")}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderTracking;
